import React, {useEffect, useCallback, useState} from "react";
import {Route} from "react-router-dom";
import {NotificationManager} from 'react-notifications';
import styled from 'styled-components';
import { ScalePrintTabs } from "./ScalePrintTabs";
import { useApi } from "../../lib/api";
import useAsync from "../../lib/use-async";
import Button from "../../components/Button";
import ListScreen from "../../components/ListScreen";
import ListItem from "../../components/ListItem";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import SideModal from "../../components/SideModal";
import Placeholder from "../../components/Placeholder";
import TimeAgo from "../../components/TimeAgo";
import Form from "../../components/Form";
import Input from "../../components/Input";
import { useUrlState } from "../../lib/with-url-state";
import search from "../../lib/search";
import { alphaSort } from "../../lib/sort";
import { T } from "../../components/I18n";

const PROTOCOL_TYPES = [
  {value: 'RICE_LAKE_EDP', label: 'Rice Lake EDP'}
]; // these need to be updated as backend is updated

function getProtocolLabel(value) {
  const match = PROTOCOL_TYPES.find(p => p.value === value);
  return match ? match.label : value;
}

export function ScaleConfigurationsTab() {
  const api = useApi();
  const [{filter, sort, protocol}, setUrlState] = useUrlState({filter: '', sort: 'asc', protocol: 'all'});
  const [getScaleConfigurations, {loading, error, result}] = useAsync(api.getScaleConfigurations);
  const [getPrinterConfigurations, {result: printersResult}] = useAsync(api.getPrinterConfigurations);
  const [getNotifications, {result: notificationsResult, error: notificationsError}] = useAsync(api.getNotifications);
  const [scaleConfigurations, setScaleConfigurations] = useState([]);
  const [printerConfigurations, setPrinterConfigurations] = useState([]);
  const [errorHistoryFor, setErrorHistoryFor] = useState(null);

  useEffect(() => {
    getScaleConfigurations();
    getPrinterConfigurations();
    getNotifications();
  }, []);

  useEffect(() => {
    setScaleConfigurations(result || []);
  }, [result]);

  useEffect(() => {
    setPrinterConfigurations(printersResult || []);
  }, [printersResult]);

  const handleSearch = useCallback((e) => {
    setUrlState({filter: e.target.value});
  }, [setUrlState]);

  const handleSortChange = useCallback((e) => {
    setUrlState({sort: e.target.value});
  }, [setUrlState]);

  const handleProtocolFilterChange = useCallback((e) => {
    setUrlState({protocol: e.target.value});
  }, [setUrlState]);

  const handleSave = useCallback((scaleConfiguration) => {
    setScaleConfigurations(prev => prev.filter(s => s.id !== scaleConfiguration.id).concat([scaleConfiguration]));
  }, []);

  const handleDelete = useCallback(async (scaleConfiguration) => {
    const attachedPrinter = printerConfigurations.find(p => p.scaleConfigurationId === scaleConfiguration.id);
    if (attachedPrinter) {
      NotificationManager.error(
        `Unable to delete scale configuration "${scaleConfiguration.serialNumber}" because it is connected to printer ${attachedPrinter.host}:${attachedPrinter.port}. Assign that printer to a different scale configuration before deleting.`
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the scale configuration "${scaleConfiguration.serialNumber}"?`)) {
      return;
    }

    try {
      await api.deleteScaleConfiguration(scaleConfiguration.id);
      setScaleConfigurations(prev => prev.filter(s => s.id !== scaleConfiguration.id));
      NotificationManager.success(<><T noTranslate>{scaleConfiguration.serialNumber}</T> was deleted.</>);
    }
    catch (e) {
      NotificationManager.error('Failed to delete scale configuration. Please try again.');
    }
  }, [api, printerConfigurations]);

  // Errors a scale reported arrive as notifications, read or not, so the history outlives the home screen card. They
  // are matched on the scale they came from, falling back to the serial for any raised without an id.
  const getErrorHistory = useCallback(scaleConfiguration => (notificationsResult || [])
    .filter(notification => notification.type === 'scale_error')
    .filter(notification => {
      const {scaleId, scaleSerial} = notification.metadata || {};
      return scaleId === scaleConfiguration.id ||
          (!scaleId && scaleSerial && scaleSerial === scaleConfiguration.serialNumber);
    })
    .sort((a, b) => b.timestamp - a.timestamp), [notificationsResult]);

  const filteredAndSortedConfigurations = scaleConfigurations
    .filter(config => search(`${config.serialNumber} ${config.sourceAddress}`, filter))
    .filter(config => protocol === 'all' || config.protocolType === protocol)
    .sort(alphaSort(sort, config => config.serialNumber || ''));

  return (
    <>
      <ListScreen
        title="Devices"
        activeLink="Devices"
        tabs={<ScalePrintTabs />}
        placeholder="Search scale configurations..."
        search={filter}
        onSearch={handleSearch}
        loading={loading && <Loading fullScreen />}
        error={error && <Error fullScreen />}
        primaryAction={<Button to="/devices/scale-configurations/create">Create Scale Configuration</Button>}
        items={filteredAndSortedConfigurations}
        filter={filter}
        toolbarActions={[
          <Input id="scale-sort" label="Sort by:" type="select" value={sort} onChange={handleSortChange}>
            <option value="asc">Alphabetical</option>
            <option value="desc">Reverse Alphabetical</option>
          </Input>,
          <Input id="scale-protocol-filter" label="Filter:" type="select" value={protocol} onChange={handleProtocolFilterChange}>
            <option value="all">All Protocols</option>
            {PROTOCOL_TYPES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Input>
        ]}
        empty={
          scaleConfigurations.length < 1 ? (
            <p>No scale configurations have been created yet. Click <b>Create Scale Configuration</b> to get started.</p>
          ) : (
            <p>No scale configurations found.</p>
          )
        }
      >
        {(config, i) => (
          <ListItem
            first={i === 0}
            options={[
              {title: 'Edit', to: `/devices/scale-configurations/${config.id}`},
              {title: 'View Errors', onClick: () => setErrorHistoryFor(config)},
              {title: 'Delete', onClick: () => handleDelete(config)}
            ]}
          >
            <ConfigRow>
              <SerialNumber><T noTranslate>{config.serialNumber}</T></SerialNumber>
              <Details>
                <span><T noTranslate>{config.sourceAddress}</T></span>
                <Separator>&middot;</Separator>
                <span>{getProtocolLabel(config.protocolType)}</span>
              </Details>
            </ConfigRow>
          </ListItem>
        )}
      </ListScreen>
      <ScaleErrorHistoryModal
        scaleConfiguration={errorHistoryFor}
        errors={errorHistoryFor ? getErrorHistory(errorHistoryFor) : []}
        failed={!!notificationsError}
        onClose={() => setErrorHistoryFor(null)}
      />
      <Route
        path="/devices/scale-configurations/:id?"
        // Keyed by the id so each account opens with a fresh form; the route also matches the list, so it stays mounted.
        render={(props) => <ManageScaleConfigurationModal key={props.match.params.id || 'new'} {...props} onSave={handleSave} />}
      />
    </>
  );
}

function ScaleErrorHistoryModal({scaleConfiguration, errors, failed, onClose}) {
  return (
    <SideModal
      open={!!scaleConfiguration}
      title="Scale Errors"
      onClose={onClose}
      options={[<Button key="close" secondary onClick={onClose}>Close</Button>]}
    >
      <SideModal.Content>
        {scaleConfiguration && (
          <ErrorHistoryIntro>
            Errors reported by <strong><T noTranslate>{scaleConfiguration.serialNumber}</T></strong>, most recent first.
          </ErrorHistoryIntro>
        )}
        {failed ? (
          <Placeholder height="6rem">Unable to load this scale's errors.</Placeholder>
        ) : errors.length < 1 ? (
          <Placeholder height="6rem">No errors have been reported to you for this scale.</Placeholder>
        ) : errors.map(error => (
          <ErrorEntry key={error.id}>
            <ErrorMessage>
              <T noTranslate>{(error.metadata || {}).errorMessage || 'An error was reported.'}</T>
            </ErrorMessage>
            <ErrorTime><TimeAgo date={error.timestamp} /></ErrorTime>
          </ErrorEntry>
        ))}
      </SideModal.Content>
    </SideModal>
  );
}

function ManageScaleConfigurationModal({history, match: {params: {id}}, onSave}) {
  const isNew = id === 'create';
  const api = useApi();
  const [getScaleConfiguration, {loading, error, result}] = useAsync(api.getScaleConfiguration);
  const [scaleConfiguration, setScaleConfiguration] = useState(createBlank());
  const [saving, setSaving] = useState(false);
  const [validationEnabled, setValidationEnabled] = useState(!isNew);

  useEffect(() => {
    if (id && !isNew) {
      getScaleConfiguration(id);
    }
  }, [id, isNew]);

  useEffect(() => {
    setScaleConfiguration(result || createBlank());
  }, [result]);

  const set = useCallback((property, value) => {
    setScaleConfiguration(prev => ({...prev, [property]: value}));
  }, []);

  const handleClose = useCallback(() => {
    history.push('/devices/scale-configurations');
  }, [history]);

  const errors = {};
  if (!scaleConfiguration.serialNumber || !scaleConfiguration.serialNumber.trim()) {
    errors.serialNumber = 'A serial number is required';
  }
  if (!scaleConfiguration.sourceAddress || !scaleConfiguration.sourceAddress.trim()) {
    errors.sourceAddress = 'A source address is required';
  }
  if (!scaleConfiguration.macAddress || ! scaleConfiguration.macAddress.trim()){
    errors.macAddress = 'A MAC address is required';
  }
  if (!scaleConfiguration.protocolType) {
    errors.protocolType = 'A protocol type is required';
  }

  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = useCallback(async () => {
    setValidationEnabled(true);

    if (hasErrors) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...scaleConfiguration,
        serialNumber: scaleConfiguration.serialNumber.trim(),
        sourceAddress: scaleConfiguration.sourceAddress.trim(),
        macAddress: scaleConfiguration.macAddress.trim()
      };
      const saved = isNew
        ? await api.createScaleConfiguration(payload)
        : await api.updateScaleConfiguration(payload);

      NotificationManager.success(<><T noTranslate>{saved.serialNumber}</T> was saved.</>);
      onSave(saved);
      history.push('/devices/scale-configurations');
    }
    catch (e) {
      NotificationManager.error('Unable to save scale configuration. Please try again.');
    }
    finally {
      setSaving(false);
    }
  }, [scaleConfiguration, hasErrors, isNew, onSave, history]);

  return (
    <Form onSubmit={handleSubmit}>
      <SideModal
        open={typeof id !== "undefined"}
        title={`${isNew ? 'Create' : 'Edit'} Scale Configuration`}
        onClose={saving ? () => {} : handleClose}
        options={[
          <Button key="save" type="submit" disabled={saving || (validationEnabled && hasErrors)}>
            {saving ? 'Saving...' : 'Save Scale Configuration'}
          </Button>,
          <Button key="cancel" secondary onClick={handleClose} disabled={saving}>Cancel</Button>
        ]}
      >
        {loading ? (
          <Loading fullScreen />
        ) : error ? (
          <Error fullScreen />
        ) : (
          <SideModal.Content>
            <Form.Group>
              <Input
                id="serial-number"
                type="text"
                label="Serial Number"
                value={scaleConfiguration.serialNumber || ''}
                onChange={e => set('serialNumber', e.target.value)}
                invalid={validationEnabled && !!errors.serialNumber}
                validationText={validationEnabled ? errors.serialNumber : ''}
              />
            </Form.Group>
            <Form.Group>
              <Input
                id="source-address"
                type="text"
                label="Source Address"
                value={scaleConfiguration.sourceAddress || ''}
                onChange={e => set('sourceAddress', e.target.value)}
                invalid={validationEnabled && !!errors.sourceAddress}
                validationText={validationEnabled ? errors.sourceAddress : ''}
              />
            </Form.Group>
            <Form.Group>
              <Input
                id="mac-address"
                type="text"
                label="MAC Address"
                placeholder="e.g: 80:A3:B2:1C:2D:3E"
                value={scaleConfiguration.macAddress || ''}
                onChange={e => set('macAddress', e.target.value)}
                invalid={validationEnabled && !!errors.macAddress}
                validationText={validationEnabled ? errors.macAddress : ''}
              />
            </Form.Group>
            <Form.Group>
              <Input
                id="protocol-type"
                type="select"
                label="Protocol Type"
                value={scaleConfiguration.protocolType || ''}
                onChange={e => set('protocolType', e.target.value)}
                invalid={validationEnabled && !!errors.protocolType}
                validationText={validationEnabled ? errors.protocolType : ''}
              >
                <option value="" disabled>Select a protocol type</option>
                {PROTOCOL_TYPES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Input>
            </Form.Group>
          </SideModal.Content>
        )}
      </SideModal>
    </Form>
  );
}

// Helper to create the basic Scale Config.
// Alter/remove when more protocols are added
function createBlank() {
  return {
    serialNumber: '',
    sourceAddress: '',
    macAddress: '',
    protocolType: 'RICE_LAKE_EDP'
  };
}

const ErrorHistoryIntro = styled.p`
  margin: 0 0 1rem 0;
  color: #747474;
  line-height: 1.5;
`;

const ErrorEntry = styled.div`
  padding: 0.75rem 0;
  border-bottom: 0.0625rem solid #e4e4e4;

  &:last-child {
    border-bottom: none;
  }
`;

const ErrorMessage = styled.div`
  line-height: 1.4;
`;

const ErrorTime = styled.small`
  display: block;
  margin-top: 0.25rem;
  color: #747474;
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SerialNumber = styled.strong`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Details = styled.small`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #747474;
`;

const Separator = styled.span`
  color: #c4c4c4;
`;